import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../utils/api';

type CrewSectionProps = {
  projectId: number;
  projectName: string;
  canWrite: boolean;
  currentUserId: number | null;
};

type User = { id: number; firstName: string; lastName: string; role: string; teamId?: number | null; isActive?: boolean };
type Assignment = { id: number; userId: number; role: string; isLead: boolean; startAt: string; endAt?: string | null; user?: User };
type Team = { id: number; name: string };

const eligibleRoles = new Set(['WORKER', 'CREW_MEMBER', 'FOREMAN']);
const displayName = (user?: User) => user ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() : 'Unknown user';
const unwrap = (res: any) => res?.data?.data ?? res?.data ?? [];
const normalizeAssignments = (res: any): { active: Assignment[]; historical: Assignment[] } => {
  const value = unwrap(res);
  return { active: Array.isArray(value?.active) ? value.active : [], historical: Array.isArray(value?.historical) ? value.historical : [] };
};
export default function ProjectCrewSection({ projectId, projectName, canWrite, currentUserId }: CrewSectionProps) {
  const [assignments, setAssignments] = useState<{ active: Assignment[]; historical: Assignment[] }>({ active: [], historical: [] });
  const [users, setUsers] = useState<User[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [open, setOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [historicalOpen, setHistoricalOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedTeam, setSelectedTeam] = useState('');
  const [selected, setSelected] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [error, setError] = useState('');

  const loadAssignments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/projects/${projectId}/assignments`);
      setAssignments(normalizeAssignments(res));
    } catch (e: any) {
      // A non-lead worker receives 403; this is also the client-side visibility gate.
      setAssignments({ active: [], historical: [] });
      if (e?.response?.status !== 403) setError(e?.response?.data?.message || 'Could not load crew.');
    } finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { loadAssignments(); }, [loadAssignments]);

  const visible = canWrite || assignments.active.some((a) => a.userId === currentUserId && a.role === 'FOREMAN' && a.isLead);
  const activeIds = useMemo(() => new Set(assignments.active.map((a) => a.userId)), [assignments.active]);
  const candidates = useMemo(() => users.filter((u) => eligibleRoles.has(u.role) && u.isActive !== false && !activeIds.has(u.id)
    && `${u.firstName} ${u.lastName}`.toLowerCase().includes(query.toLowerCase())
    && (!selectedTeam || String(u.teamId ?? '') === selectedTeam)), [users, activeIds, query, selectedTeam]);

  const openPicker = async () => {
    setPickerOpen(true); setPickerLoading(true); setError('');
    try {
      // S3 fix (Cash-verify): /auth/users is ADMIN/SUPER_ADMIN-only and 403'd
      // this picker for every real intended user (PM/CM/SM and lead foremen).
      // /assignments/eligible-workers is the narrow, non-admin-gated list.
      const [usersRes, teamsRes] = await Promise.all([api.get('/assignments/eligible-workers'), api.get('/projects/crews')]);
      setUsers(unwrap(usersRes));
      setTeams(unwrap(teamsRes));
    } catch (e: any) { setError(e?.response?.data?.message || 'Could not load company workers.'); }
    finally { setPickerLoading(false); }
  };

  const addUsers = async (ids: number[]) => {
    if (!ids.length) return;
    setError('');
    try {
      await Promise.all(ids.map((userId) => {
        const user = users.find((u) => u.id === userId);
        return api.post(`/projects/${projectId}/assignments`, { userId, role: user?.role === 'FOREMAN' ? 'FOREMAN' : 'WORKER', isLead: false });
      }));
      setSelected([]); setPickerOpen(false); await loadAssignments();
    } catch (e: any) { setError(e?.response?.data?.message || 'Could not add worker.'); }
  };

  const dismiss = async (assignmentId: number) => {
    setError('');
    try { await api.patch(`/assignments/${assignmentId}`, { endAt: new Date().toISOString() }); await loadAssignments(); }
    catch (e: any) { setError(e?.response?.data?.message || 'Could not dismiss worker.'); }
  };

  if (!visible) return null;
  return <section data-testid={`crew-section-${projectId}`} className="mt-5 pt-4 border-t border-gray-100">
    <div className="flex items-center justify-between gap-2">
      <h3 className="font-semibold text-gray-900">Crew</h3>
      <div className="flex gap-2">
        {canWrite && <button data-testid={`add-worker-${projectId}`} onClick={openPicker} className="px-3 py-1.5 text-xs rounded-lg bg-blue-600 text-white">Add Worker</button>}
        <button data-testid={`crew-toggle-${projectId}`} onClick={() => setOpen(!open)} className="text-xs text-gray-500">{open ? 'Hide' : 'Show'}</button>
      </div>
    </div>
    {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
    {open && <>
      {loading ? <p className="py-3 text-xs text-gray-500">Loading crew…</p> : <>
        <div className="mt-3 divide-y divide-gray-100 border border-gray-100 rounded-lg">
          {assignments.active.length === 0 ? <p className="p-3 text-xs text-gray-500">No active crew assigned.</p> : assignments.active.map((a) => <div key={a.id} className="flex items-center justify-between px-3 py-2">
            <div><span className="text-sm font-medium">{displayName(a.user)}</span><span className="ml-2 text-xs text-gray-500">{a.role}</span>{a.isLead && <span className="ml-2 text-xs text-amber-600">★ Lead</span>}</div>
            {visible && <button data-testid={`dismiss-${a.id}`} onClick={() => dismiss(a.id)} className="text-xs text-red-600">Dismiss</button>}
          </div>)}
        </div>
        <button data-testid={`historical-toggle-${projectId}`} onClick={() => setHistoricalOpen(!historicalOpen)} className="mt-3 text-xs font-medium text-gray-600">{historicalOpen ? 'Hide' : 'Show'} dismissed ({assignments.historical.length})</button>
        {historicalOpen && <div data-testid={`historical-${projectId}`} className="mt-2 divide-y divide-gray-100 border border-gray-100 rounded-lg">{assignments.historical.map((a) => <div key={a.id} className="px-3 py-2 text-xs"><span className="font-medium">{displayName(a.user)}</span> · {a.role} · ended {a.endAt ? new Date(a.endAt).toLocaleDateString() : '—'}</div>)}</div>}
      </>}
    </>}
    {pickerOpen && <div data-testid={`worker-picker-${projectId}`} className="mt-3 rounded-lg border border-blue-100 bg-blue-50 p-3">
      <div className="flex items-center justify-between"><h4 className="text-sm font-semibold">Add worker to {projectName}</h4><button onClick={() => setPickerOpen(false)} className="text-xs">Close</button></div>
      {pickerLoading ? <p className="py-3 text-xs">Loading workers…</p> : <>
        <div className="mt-2 flex gap-2"><input aria-label="Search workers" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name" className="min-w-0 flex-1 px-2 py-1.5 text-xs border rounded" /><select aria-label="Team preset" value={selectedTeam} onChange={(e) => setSelectedTeam(e.target.value)} className="px-2 py-1.5 text-xs border rounded"><option value="">All teams</option>{teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</select></div>
        <div className="mt-2 max-h-48 overflow-auto divide-y divide-blue-100">{candidates.map((u) => <label key={u.id} className="flex items-center gap-2 px-1 py-2 text-xs"><input type="checkbox" checked={selected.includes(u.id)} onChange={() => setSelected((old) => old.includes(u.id) ? old.filter((id) => id !== u.id) : [...old, u.id])} /><span className="font-medium">{displayName(u)}</span><span className="text-gray-500">{u.role}</span></label>)}{!candidates.length && <p className="py-2 text-xs text-gray-500">No eligible people or crews to assign yet. Import your team on the Team page (Team → Import CSV) or add workers one at a time — new people appear here automatically.</p>}</div>
        <button data-testid={`add-selected-${projectId}`} onClick={() => addUsers(selectedTeam ? candidates.filter((u) => selected.includes(u.id)).map((u) => u.id) : selected)} disabled={!selected.length} className="mt-2 px-3 py-1.5 text-xs rounded bg-blue-600 text-white disabled:opacity-50">Add Selected</button>
      </>}
    </div>}
  </section>;
}
